import React, { Component } from 'react';
import * as yup from 'yup';

import Modal, {
  ModalAction,
  ModalBody,
  ModalButton,
  ModalHeader,
} from '../../../../components/Modal/Modal';
import { trans } from '../../../../localization/index';
import WalletBasicInfo from '../components/WalletBasicInfo';
import WalletRestaurantInfo from '../components/WalletRestaurantInfo';
import {
  clearSpecialCharacters,
  isEmpty,
  validateEmail,
  validatePhone,
} from '../../../../utils/utils';
import { EnumWalletServiceType } from '../../../../constants/enum';

const stateData = {
  ownerName: '',
  ownerPhone: '',
  ownerEmail: '',
  idCardNumber: '',
  address: '',
  restaurant: { value: 0, label: '' },
  restaurantName: '',
  restaurantPhone: '',
  restaurantAddress: '',
  restaurantEmail: '',
  customerName: '',
  airpayContact: false,
  maskServiceTypes: {
    label: EnumWalletServiceType.ALL.label,
    value: EnumWalletServiceType.ALL.id,
  },
  isSubmit: false,
};

class CreateWalletModal extends Component {
  state = stateData;

  componentDidUpdate(prevProps) {
    this.handleOpenDialog(prevProps);
  }

  handleOpenDialog = (prevProps = {}) => {
    const { isOpenDialog } = this.props;
    const { isOpenDialog: oldIsOpenDialog } = prevProps;
    // check change state from close -> open
    if (!oldIsOpenDialog && isOpenDialog) {
      this.resetState();
    }
  };

  resetState = () => {
    const intStateData = { ...stateData };
    intStateData.customerName = this.props.user.name;
    this.setState(intStateData);
  };

  handleChangeInput = stateObj => {
    this.setState(stateObj);
  };

  handleChangeRestaurant = value => {
    const { restaurant } = this.state;
    if (value === null || value.value !== restaurant.value) {
      const {
        airpay_contract: airpayContact = false,
        name,
        email,
        phone,
        address,
      } = value.data;
      const restaurantPhone = clearSpecialCharacters(phone);
      this.setState({
        restaurant: value,
        airpayContact,
        restaurantName: name,
        restaurantPhone: validatePhone(restaurantPhone) ? restaurantPhone : '',
        restaurantAddress: address,
        restaurantEmail: validateEmail(email) ? email : '',
      });
    }
  };

  handleCreateWallet = () => {
    const { setGeneralError, createUserWallet } = this.props;
    this.setState({
      isSubmit: true,
    });

    function validate(reqParam, dispatchGeneralError) {
      const {
        owner_phone: ownerPhone,
        restaurant_phone: restaurantPhone,
      } = reqParam;
      const requiredMessage = trans('mwl_required_fill_information');
      const schema = yup.object().shape({
        restaurant_id: yup
          .number()
          .min(1, trans('mwl_choose_restaurant'))
          .required(requiredMessage),
        restaurant_name: yup
          .string()
          .required(requiredMessage)
          .test(
            'len',
            trans('mwl_invalid_restaurant_name'),
            val => val.length >= 2 && val.length <= 256,
          ),
        restaurant_address: yup
          .string()
          .required(requiredMessage)
          .test(
            'len',
            trans('mwl_invalid_address'),
            val => val.length >= 10 && val.length <= 100,
          ),
        owner_name: yup
          .string()
          .required(requiredMessage)
          .test(
            'len',
            trans('mwl_invalid_owner_name'),
            val => val.length >= 2 && val.length <= 100,
          ),
        owner_phone: yup.string().required(requiredMessage),
        owner_email: yup
          .string()
          .required(requiredMessage)
          .test('email', trans('mwl_invalid_email'), validateEmail)
          .test('len', trans('mql_invalid_email'), val => val.length <= 100),
        restaurant_email: yup
          .string()
          .test('email', trans('mwl_invalid_email'), validateEmail)
          .test('len', trans('mql_invalid_email'), val => val.length <= 100),
        id_card_number: yup
          .string()
          .required(requiredMessage)
          .test(
            'len',
            trans('mwl_invalid_card_number'),
            val => val.length >= 2 && val.length <= 16,
          ),
        address: yup
          .string()
          .required(requiredMessage)
          .test(
            'len',
            trans('mwl_invalid_address'),
            val => val.length >= 10 && val.length <= 100,
          ),
        customer_name: yup
          .string()
          .test(
            'len',
            trans('mwl_invalid_customer_name'),
            val => val.length === 0 || (val.length >= 2 && val.length <= 64),
          ),
      });
      try {
        schema.validateSync(reqParam);
        if (
          !validatePhone(ownerPhone) ||
          (!isEmpty(restaurantPhone) && !validatePhone(restaurantPhone))
        ) {
          dispatchGeneralError('merchant_phone_invalid');
          return false;
        }
        return true;
      } catch (err) {
        dispatchGeneralError(err.message);
        return false;
      }
    }

    function genBody(state) {
      const {
        ownerName,
        ownerPhone,
        ownerEmail,
        idCardNumber,
        address,
        restaurant,
        restaurantName,
        restaurantPhone,
        restaurantAddress,
        restaurantEmail,
        maskServiceTypes,
        customerName,
      } = state;
      const reqParam = {
        owner_name: ownerName,
        owner_phone: clearSpecialCharacters(ownerPhone),
        owner_email: ownerEmail,
        id_card_number: clearSpecialCharacters(idCardNumber),
        address,
        restaurant_id: restaurant.value,
        restaurant_name: restaurantName,
        restaurant_address: restaurantAddress,
        restaurant_email: restaurantEmail,
        customer_name: customerName,
        restaurant_phone: clearSpecialCharacters(restaurantPhone),
        mask_service_types: maskServiceTypes.value,
      };
      return reqParam;
    }

    const reqParam = genBody(this.state);
    if (validate(reqParam, setGeneralError)) {
      createUserWallet(reqParam);
    }
  };

  render() {
    const {
      isOpenDialog,
      unlinkedRestaurants,
      closeDialogGeneric,
    } = this.props;
    const {
      ownerName,
      ownerPhone,
      ownerEmail,
      idCardNumber,
      address,
      restaurant,
      restaurantName,
      restaurantPhone,
      restaurantAddress,
      restaurantEmail,
      customerName,
      airpayContact,
      maskServiceTypes,
      isSubmit,
    } = this.state;
    return (
      <React.Fragment>
        <Modal
          isOpen={isOpenDialog}
          onRequestClose={closeDialogGeneric}
          className="edit-merchant-form"
        >
          <ModalHeader>
            {trans('rebranding.merchant_wallet_create_user_wallet')}
          </ModalHeader>
          <ModalBody>
            <p className="title-block">
              {trans('merchant_wallet_add_restaurant_info')}
            </p>
            <WalletRestaurantInfo
              restaurant={restaurant}
              restaurantName={restaurantName}
              restaurantPhone={restaurantPhone}
              restaurantAddress={restaurantAddress}
              restaurantEmail={restaurantEmail}
              customerName={customerName}
              handleChangeRestaurant={this.handleChangeRestaurant}
              unlinkedRestaurants={unlinkedRestaurants}
              airpayContact={airpayContact}
              isSubmit={isSubmit}
              handleChangeInput={this.handleChangeInput}
            />
            <p className="title-block">
              {trans('merchant_wallet_add_basic_info')}
            </p>
            <WalletBasicInfo
              ownerName={ownerName}
              ownerPhone={ownerPhone}
              ownerEmail={ownerEmail}
              idCardNumber={idCardNumber}
              address={address}
              isSubmit={isSubmit}
              maskServiceTypes={maskServiceTypes}
              required
              handleChangeInput={this.handleChangeInput}
            />
          </ModalBody>
          <ModalAction>
            <ModalButton type="cancel" onClick={closeDialogGeneric}>
              {trans('common_close')}
            </ModalButton>
            <ModalButton styleType="red" onClick={this.handleCreateWallet}>
              {trans('merchant_wallet_add')}
            </ModalButton>
          </ModalAction>
        </Modal>
      </React.Fragment>
    );
  }
}

export default CreateWalletModal;



// WEBPACK FOOTER //
// ./foody/src/pages/MerchantWallet/WalletManagement/CreateWallet/CreateWalletModal.jsx